import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import {
  ReactNode,
  useReducer,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from 'react';
import { queryClient } from '@/utils/trpc';
import {
  useInactivityTimeout,
  INACTIVITY_STORAGE_KEY,
} from '@/hooks/useInactivityTimeout';
import { useCrossTabSignOut } from '@/hooks/useCrossTabSignOut';
import { useAmplifyAuthHub } from '@/hooks/useAmplifyAuthHub';

export interface UserAttributes {
  email?: string;
  email_verified?: boolean;
  sub?: string;
  [key: string]: string | boolean | undefined;
}

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserAttributes | null;
  handleSignOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

type AuthState = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserAttributes | null;
};

type AuthAction =
  | { type: 'AUTHENTICATED'; user: UserAttributes }
  | { type: 'UNAUTHENTICATED' };

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
};

const authReducer = (_state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTHENTICATED':
      return { isAuthenticated: true, isLoading: false, user: action.user };
    case 'UNAUTHENTICATED':
      return { isAuthenticated: false, isLoading: false, user: null };
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialAuthState);

  const handleSignOut = useCallback(async () => {
    localStorage.removeItem(INACTIVITY_STORAGE_KEY);
    await signOut().catch((err) =>
      console.error('Error during sign out:', err),
    );
    queryClient.clear();
    dispatch({ type: 'UNAUTHENTICATED' });
  }, []);

  // Track inactivity (disabled in local dev to avoid repeated sign-outs)
  const isDevMode = import.meta.env.DEV;
  useInactivityTimeout(
    !isDevMode && state.isAuthenticated && !state.isLoading,
    handleSignOut,
  );

  // Track cross-tab sign out
  useCrossTabSignOut(handleSignOut);

  const checkUser = useCallback(async () => {
    try {
      const { userId, signInDetails } = await getCurrentUser();
      const session = await fetchAuthSession();

      if (session?.tokens) {
        const email =
          signInDetails?.loginId ??
          (session.tokens.idToken?.payload?.email as string | undefined);

        dispatch({ type: 'AUTHENTICATED', user: { email, sub: userId } });
      } else {
        dispatch({ type: 'UNAUTHENTICATED' });
      }
    } catch {
      localStorage.removeItem(INACTIVITY_STORAGE_KEY);
      dispatch({ type: 'UNAUTHENTICATED' });
    }
  }, []);

  // Handle existing session load
  useEffect(() => {
    const url = new URL(window.location.href);
    const hasOAuthCallback =
      url.searchParams.has('state') &&
      (url.searchParams.has('code') || url.searchParams.has('error'));

    if (hasOAuthCallback) {
      return;
    }

    checkUser();
  }, [checkUser]);

  const handleAuthFailure = useCallback(() => {
    dispatch({ type: 'UNAUTHENTICATED' });
  }, []);

  useAmplifyAuthHub(checkUser, handleAuthFailure);

  return (
    <AuthContext.Provider value={{ ...state, handleSignOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
